import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  mergeStyleSets,
} from "@fluentui/react";
import { CaretLeft, Robot, UsersThree, MapPin, Calendar, Info } from "@phosphor-icons/react";
import IconBadge from "../components/common/IconBadge";
import { getPhosphorIcon } from "../utils/iconMap";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";
import { useNavigate } from "react-router-dom";

// ── Types ─────────────────────────────────────────────
interface Sa2Area { sa2_code: string; sa2_name: string; area_sqkm: number }

interface Kpis {
  total_population: number;
  total_dwellings: number;
  avg_household_size: number;
  growth_pct: number;
  forecast_year: number;
}

// ── Constants ─────────────────────────────────────────
const COLORS = ["#6366F1","#38BDF8","#34D399","#FBBF24","#F472B6","#A78BFA","#FB923C","#06B6D4","#E11D48","#8B5CF6","#10B981","#F59E0B","#14B8A6","#D946EF"];

const YEAR_OPTIONS: IDropdownOption[] = [
  { key: "2041", text: "2041" },
  { key: "2036", text: "2036" },
  { key: "2031", text: "2031" },
  { key: "2026", text: "2026" },
  { key: "2021", text: "2021 (Baseline)" },
];

const METRIC_OPTIONS: IDropdownOption[] = [
  { key: "population_total", text: "Total Population" },
  { key: "dwellings_total", text: "Total Dwellings" },
  { key: "persons_65_plus", text: "Persons 65+" },
  { key: "persons_0_14", text: "Persons 0-14" },
  { key: "net_migration", text: "Net Migration" },
];

const tooltipStyle = { backgroundColor: "#fff", border: "1px solid #E1E1E1", borderRadius: 4, color: "#232323", fontSize: 12 };

// ── Helpers ───────────────────────────────────────────
function fmtNum(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : n.toLocaleString("en-AU");
}
function fmtPct(v: number | string | null | undefined): string {
  if (v == null) return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : (n >= 0 ? "+" : "") + n.toFixed(1) + "%";
}
function truncLabel(s: string, max = 18): string {
  return s.length > max ? s.slice(0, max) + "..." : s;
}

// ── Styles ───────────────────────────────────────────
const p = boroondaraPalette;
const styles = mergeStyleSets({
  page: { padding: 32, minHeight: "calc(100vh - 52px)", background: "#F5F5F5", "@media (max-width: 768px)": { padding: 16 } } as any,
  container: { maxWidth: 1280, margin: "0 auto" },
  headerRow: { marginBottom: 24 },
  title: { color: p.text, fontWeight: 800 as any, fontSize: 28, letterSpacing: "-0.02em" },
  subtitle: { color: p.text2, marginTop: 6, maxWidth: 780, lineHeight: "1.6", fontSize: 14 },
  chipRow: { display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" } as any,
  chip: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "#fff", border: "1px solid #E1E1E1", borderRadius: 16,
    padding: "4px 12px", fontSize: 12, color: p.text2,
  },
  filterRow: {
    display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap", alignItems: "flex-end",
    background: "#fff", borderRadius: 8, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  } as any,
  filterLabel: { fontSize: 12, fontWeight: 600, color: p.text2, marginBottom: 4 },
  kpiGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 16, marginBottom: 28,
    "@media (max-width: 768px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  } as any,
  kpiCard: {
    background: "#fff", borderRadius: 10, padding: "20px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 4,
  } as any,
  kpiLabel: { fontSize: 12, color: p.text2, fontWeight: 600 },
  kpiValue: { fontSize: 24, fontWeight: 800 as any, color: p.text },
  kpiSub: { fontSize: 11, color: p.text3 },
  chartsRow: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28,
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  } as any,
  chartCard: {
    background: "#fff", borderRadius: 10, padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  chartTitle: { fontSize: 14, fontWeight: 700, color: p.text, marginBottom: 12 },
  tableWrap: {
    background: "#fff", borderRadius: 10, padding: 20, marginBottom: 28,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflowX: "auto",
  } as any,
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 } as any,
  th: { textAlign: "left" as any, padding: "8px 10px", borderBottom: "2px solid #E1E1E1", color: p.text2, fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" as any },
  td: { padding: "7px 10px", borderBottom: "1px solid #F0F0F0", color: p.text, whiteSpace: "nowrap" as any },
  agentCta: {
    background: "linear-gradient(135deg, #6366F1, #818CF8)", borderRadius: 12, padding: "24px 28px",
    display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer",
    transition: "transform 0.15s ease", ":hover": { transform: "translateY(-2px)" },
  } as any,
  agentCtaText: { color: "#fff" },
  agentCtaTitle: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  agentCtaSub: { fontSize: 13, opacity: 0.85 },
  agentCtaIcon: { fontSize: 32, color: "#fff", opacity: 0.9 },
  error: { marginBottom: 20 },
});

// ══════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════
export default function PopulationForecasts() {
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [sa2Areas, setSa2Areas] = useState<Sa2Area[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [comparisonData, setComparisonData] = useState<any[]>([]);
  const [forecastData, setForecastData] = useState<any[]>([]);

  const [year, setYear] = useState("2041");
  const [sa2, setSa2] = useState("all");
  const [selectedMetric, setSelectedMetric] = useState("population_total");

  // ── Fetch KPIs + SA2 areas ──
  const fetchBase = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [kpiRes, sa2Res] = await Promise.all([
        accessService.get(`/population/kpis?year=${year}`),
        accessService.get("/abs/sa2-areas"),
      ]);
      setKpis(kpiRes.data);
      setSa2Areas(sa2Res.data);
    } catch (err: any) {
      setError(err.message || "Failed to load population data");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchBase(); }, [fetchBase]);

  // ── Fetch trend data ──
  const fetchTrend = useCallback(async () => {
    try {
      const params = sa2 !== "all" ? `?sa2=${sa2}` : "";
      const res = await accessService.get(`/population/trend${params}`);
      setTrendData(res.data);
    } catch (err: any) {
      console.error("Trend error:", err);
    }
  }, [sa2]);

  useEffect(() => { fetchTrend(); }, [fetchTrend]);

  // ── Fetch comparison data ──
  const fetchComparison = useCallback(async () => {
    try {
      const res = await accessService.get(`/population/comparison?year=${year}&metric=${selectedMetric}`);
      setComparisonData(res.data);
    } catch (err: any) {
      console.error("Comparison error:", err);
    }
  }, [year, selectedMetric]);

  useEffect(() => { fetchComparison(); }, [fetchComparison]);

  // ── Fetch forecast table data ──
  const fetchForecasts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set("year", year);
      if (sa2 !== "all") params.set("sa2", sa2);
      const res = await accessService.get(`/population/forecasts?${params}`);
      setForecastData(res.data);
    } catch (err: any) {
      console.error("Forecast data error:", err);
    }
  }, [year, sa2]);

  useEffect(() => { fetchForecasts(); }, [fetchForecasts]);

  // ── Dropdown options ──
  const sa2Options: IDropdownOption[] = useMemo(() => [
    { key: "all", text: "All Boroondara" },
    ...sa2Areas.map(a => ({ key: a.sa2_code, text: a.sa2_name })),
  ], [sa2Areas]);

  // ── Pie data: age cohort from forecast data ──
  const pieData = useMemo(() => {
    if (forecastData.length === 0) return [];
    const fields = [
      { key: "persons_0_14", label: "0-14" },
      { key: "persons_15_24", label: "15-24" },
      { key: "persons_25_44", label: "25-44" },
      { key: "persons_45_64", label: "45-64" },
      { key: "persons_65_plus", label: "65+" },
    ];
    const agg: Record<string, number> = {};
    fields.forEach(f => { agg[f.key] = 0; });
    forecastData.forEach(row => {
      fields.forEach(f => { agg[f.key] += Number(row[f.key]) || 0; });
    });
    return fields.map(f => ({ name: f.label, value: agg[f.key] })).filter(d => d.value > 0);
  }, [forecastData]);

  // ── KPI cards ──
  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    return [
      { icon: "People", color: "#6366F1", label: "Projected Population", value: fmtNum(kpis.total_population), sub: `${kpis.forecast_year} forecast` },
      { icon: "Up", color: "#34D399", label: "Growth from 2021", value: fmtPct(kpis.growth_pct), sub: "vs 2021 baseline" },
      { icon: "Home", color: "#FBBF24", label: "Total Dwellings", value: fmtNum(kpis.total_dwellings), sub: `${kpis.forecast_year} projection` },
      { icon: "Group", color: "#38BDF8", label: "Avg Household Size", value: kpis.avg_household_size != null ? Number(kpis.avg_household_size).toFixed(1) : "-", sub: "persons per dwelling" },
    ];
  }, [kpis]);

  // ══════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════
  if (loading && !kpis) {
    return (
      <div className={styles.page}>
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}>
          <Spinner size={SpinnerSize.large} label="Loading population forecast data..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Breadcrumb ── */}
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => nav('/dashboards')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: '#6366F1', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <CaretLeft size={10} weight="bold" /> Dashboards
          </button>
        </div>

        {/* ── Header ── */}
        <div className={styles.headerRow}>
          <div className={styles.title}>Population Forecasts</div>
          <div className={styles.subtitle}>
            Population and dwelling forecasts for the City of Boroondara (2021-2041).
            Explore growth projections, age cohort trends, household size and migration
            across {sa2Areas.length} SA2 statistical areas.
          </div>
          <div className={styles.chipRow}>
            <span className={styles.chip}><UsersThree size={12} weight="regular" /> Population Forecasts</span>
            <span className={styles.chip}><MapPin size={12} weight="regular" /> {sa2Areas.length} SA2 Areas</span>
            <span className={styles.chip}><Calendar size={12} weight="regular" /> 2021-2041</span>
            <span className={styles.chip}><Info size={12} weight="regular" /> Source: forecast.id</span>
          </div>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={styles.error}>{error}</MessageBar>
        )}

        {/* ── Filters ── */}
        <div className={styles.filterRow}>
          <div>
            <div className={styles.filterLabel}>Forecast Year</div>
            <Dropdown
              selectedKey={year}
              options={YEAR_OPTIONS}
              onChange={(_, opt) => opt && setYear(String(opt.key))}
              styles={{ root: { width: 180 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>SA2 Area</div>
            <Dropdown
              selectedKey={sa2}
              options={sa2Options}
              onChange={(_, opt) => opt && setSa2(String(opt.key))}
              styles={{ root: { width: 220 } }}
            />
          </div>
          <div>
            <div className={styles.filterLabel}>Chart Metric</div>
            <Dropdown
              selectedKey={selectedMetric}
              options={METRIC_OPTIONS}
              onChange={(_, opt) => opt && setSelectedMetric(String(opt.key))}
              styles={{ root: { width: 220 } }}
            />
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className={styles.kpiGrid}>
          {kpiCards.map((kpi, i) => (
            <div key={i} className={styles.kpiCard}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <IconBadge icon={getPhosphorIcon(kpi.icon)} size="sm" color={kpi.color} weight="fill" />
                <span className={styles.kpiLabel}>{kpi.label}</span>
              </div>
              <div className={styles.kpiValue} style={{ color: kpi.color }}>{kpi.value}</div>
              <div className={styles.kpiSub}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Charts ── */}
        <div className={styles.chartsRow}>
          {/* Line chart: population trend */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>
              Population Trend {sa2 !== "all" ? `- ${sa2Options.find(o => o.key === sa2)?.text}` : "- All Boroondara"}
            </div>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={trendData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="forecast_year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => (v / 1000).toFixed(0) + "K"} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="population_total" name="Population" stroke="#6366F1" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="dwellings_total" name="Dwellings" stroke="#FBBF24" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No trend data available
              </div>
            )}
          </div>

          {/* Bar chart: comparison across SA2 areas */}
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>
              {METRIC_OPTIONS.find(m => m.key === selectedMetric)?.text || selectedMetric} by SA2 Area ({year})
            </div>
            {comparisonData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={comparisonData} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis dataKey="sa2_name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0}
                    tickFormatter={(v: string) => truncLabel(v, 14)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {comparisonData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No comparison data available
              </div>
            )}
          </div>
        </div>

        {/* ── Pie chart: Age cohort ── */}
        <div className={styles.chartsRow}>
          <div className={styles.chartCard}>
            <div className={styles.chartTitle}>
              Age Cohort Breakdown ({year}) {sa2 !== "all" ? `- ${sa2Options.find(o => o.key === sa2)?.text}` : "- All Areas"}
            </div>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={120} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={true} fontSize={11}>
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtNum(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center", color: p.text3 }}>
                No age cohort data available
              </div>
            )}
          </div>
          <div /> {/* empty grid cell */}
        </div>

        {/* ── Data Table ── */}
        <div className={styles.tableWrap}>
          <div className={styles.chartTitle}>
            Forecast Data — {year}
            {sa2 !== "all" && ` — ${sa2Options.find(o => o.key === sa2)?.text}`}
          </div>
          {forecastData.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Suburb</th>
                  <th className={styles.th}>Population</th>
                  <th className={styles.th}>Male</th>
                  <th className={styles.th}>Female</th>
                  <th className={styles.th}>Dwellings</th>
                  <th className={styles.th}>HH Size</th>
                  <th className={styles.th}>0-14</th>
                  <th className={styles.th}>15-24</th>
                  <th className={styles.th}>25-44</th>
                  <th className={styles.th}>45-64</th>
                  <th className={styles.th}>65+</th>
                </tr>
              </thead>
              <tbody>
                {forecastData.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff" }}>
                    <td className={styles.td}>{row.sa2_name}</td>
                    <td className={styles.td}>{fmtNum(row.population_total)}</td>
                    <td className={styles.td}>{fmtNum(row.population_male)}</td>
                    <td className={styles.td}>{fmtNum(row.population_female)}</td>
                    <td className={styles.td}>{fmtNum(row.dwellings_total)}</td>
                    <td className={styles.td}>{row.avg_household_size}</td>
                    <td className={styles.td}>{fmtNum(row.persons_0_14)}</td>
                    <td className={styles.td}>{fmtNum(row.persons_15_24)}</td>
                    <td className={styles.td}>{fmtNum(row.persons_25_44)}</td>
                    <td className={styles.td}>{fmtNum(row.persons_45_64)}</td>
                    <td className={styles.td}>{fmtNum(row.persons_65_plus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: p.text3 }}>
              No data available for the selected filters.
            </div>
          )}
        </div>

        {/* ── Agent CTA ── */}
        <div className={styles.agentCta} onClick={() => nav("/chat")} role="button" tabIndex={0}>
          <div className={styles.agentCtaText}>
            <div className={styles.agentCtaTitle}>Ask the Population Forecast Agent</div>
            <div className={styles.agentCtaSub}>
              Get instant answers about Boroondara population projections — growth trends, age cohort shifts, dwelling forecasts and more.
            </div>
          </div>
          <Robot size={32} weight="duotone" color="#fff" style={{ opacity: 0.9 }} />
        </div>
      </div>
    </div>
  );
}
